import { PrintMarkerClip } from '../print/PrintMarkerClip'
import { LineNameMarker } from '../LineNameMarker'
import { useMemo } from 'react'
import { GeoJsonLineLayer, MapMarker } from '@wehunt/map'
import { isLineKindVisible, pinLabelColors } from '../../lib/mapAppearance'
import { pinTypeVisibility } from '../../lib/mapPinTypes'
import type { MapAppearance } from '../../lib/mapAppearance'
import { MapPinSprite, pinMarkerCentreLiftPx, useDPRScale } from '../MapPin'
import { lineDashPattern, lineFeatureCollection, lineWidthExpression, segmentBearingDeg } from '../../lib/mapGeoJson'
import { LINE_BORDER_FACTOR, LINE_DEFAULT_COLOR, MAPLIBRE_LAW_ZOOM_OFFSET, lineStrokeWidthCssPx } from '../../lib/mapLines'
import { pinLabelCssPx } from '../../lib/geo'
import { usePrintPinPreview } from '../print/usePrintPinPreview'
import { printPinLabelStyle, type PrintPinPreviewSpec } from '../../lib/printPinPreview'
import type { GeoLine, GeoLineStyle, GeoPt, MapObject } from '../../lib/types'
import { LINES_CASING_ID, LINES_HIT_ID, LINES_ID } from './editorLayerIds'
import type { LayerId } from './mapLibreEditorTypes'

/** Every drawn-line style, in a fixed order — mirrors `TeamMapLayers`'s own
 *  list, which this file deliberately duplicates rather than importing: that
 *  component's list is a private module constant, not exported, and the two
 *  surfaces have no other shared coupling worth introducing for four strings.
 *  (The dash PATTERN table itself is shared — `lineDashPattern`,
 *  `lib/mapGeoJson.ts` — because that one carries a width-relative-units
 *  correction a casing/interior pair must never independently drift on.) */
const LINE_STYLES: readonly GeoLineStyle[] = ['solid', 'dashed', 'dotted', 'dash_dot']
const ARROW_HALF_ANGLE = Math.PI / 8
/** The app's screen-space cap on an arrowhead's size (WET-4184 parity). */
const ARROW_MAX_HEIGHT = 56

function samePoint(a: GeoPt, b: GeoPt): boolean {
  return a.lat === b.lat && a.lon === b.lon
}

function secondLastDistinctPoint(points: readonly GeoPt[]): GeoPt | null {
  const last = points[points.length - 1]
  for (let i = points.length - 2; i >= 0; i--) {
    if (!samePoint(points[i], last)) return points[i]
  }
  return null
}

function ArrowHeadGlyph({ color, height }: { color: string; height: number }) {
  const width = 2 * height * Math.tan(ARROW_HALF_ANGLE)
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', overflow: 'visible' }}>
      <polygon points={`${width / 2},0 0,${height} ${width},${height}`} fill={color} stroke="#000" strokeWidth={1} />
    </svg>
  )
}

export interface EditorPinsAndLinesProps {
  objects: MapObject[]
  lines: GeoLine[]
  selectedId: string | null
  activeTab: LayerId
  appearance: MapAppearance
  onSelectObj: (id: string | null) => void
  onMoveObj: (id: string, lat: number, lon: number) => void
  onLineSelect?: (id: string) => void
  /** True while drawing/placing/vertex-editing — every pin and line goes
   *  inert, mirroring `FullscreenMapCanvas`'s `blockInteraction`. */
  blockInteraction: boolean
  /** The id of a line currently lifted into `VertexEditSession`, if any —
   *  suppressed from BOTH the visible line layers and the lines hit layer
   *  (they share `linesFC`) so it doesn't double-render under the session's
   *  own overlay. Mirrors `FullscreenMapCanvas`'s own `vertexEdit?.kind ===
   *  "line" && vertexEdit.id === l.id` skip (`MapEditorPage.tsx:4019`). */
  vertexEditLineId?: string
  /** CLUB-676: pins and lines the viewer has hidden from the collection panel;
   *  neither their sprites nor their strokes, arrowheads or name labels are
   *  drawn. Display state only — it never enters the edited map. */
  hiddenObjectIds?: ReadonlySet<string>
  /** Live zoom — arrowhead sizing follows the line-width law, and pin label
   *  size follows `pinLabelCssPx(zoom, tileZoomMax)`. */
  zoom: number
  tileZoomMax: number
  /** The print sheet's corner ring while the print panel is open, outside
   *  which pins are drawn faint — see `printDim.ts` for why the frame's own
   *  dim layer cannot reach a DOM marker. Null/omitted draws every pin at
   *  full strength. */
  dimOutside?: GeoPt[] | null
  /** While print mode is on: pins and their names are drawn at the size the
   *  sheet will carry them, opaque, with names white-on-black as the server
   *  prints them, in place of the Pin size, pin opacity and label options — see
   *  `printPinPreview`. Omitted draws the editor's everyday pins. */
  printPinSizes?: PrintPinPreviewSpec
}

/** Committed pins (draggable `MapMarker`s) and drawn lines/arrows (the same
 *  spec-1 `mapGeoJson` layers `TeamMapLayers` uses, plus a wide hit layer for
 *  the click router and per-line name labels). NOTE: MapLibre suppresses the
 *  `click` event that would otherwise follow a real marker drag (verified in
 *  Task 1/testing) — so, unlike the old SVG canvas, no `hasDragged` guard is
 *  needed here to stop a drag's release from also firing a select. */
export function EditorPinsAndLines({
  objects,
  lines,
  selectedId,
  activeTab,
  appearance,
  onSelectObj,
  onMoveObj,
  onLineSelect,
  blockInteraction,
  vertexEditLineId,
  hiddenObjectIds,
  zoom,
  tileZoomMax,
  dimOutside,
  printPinSizes,
}: EditorPinsAndLinesProps) {
  const lt = appearance.lineThickness
  const dprScale = useDPRScale()
  const printPreview = usePrintPinPreview(printPinSizes, appearance.pinBackground)
  const pinScale = printPreview?.pinScale ?? appearance.pinScale
  // CLUB-667: shared with the read-only map and the print payload, so no
  // surface re-implements which pin types are drawn.
  const pinVisible = pinTypeVisibility(appearance.hiddenPinTypes)
  const arrowsShown = appearance.arrowsVisible || activeTab === 'arrows'
  const linesShown = appearance.linesVisible || activeTab === 'lines'

  // A line under active vertex-edit is suppressed here (Task 4) — its
  // `VertexEditSession` overlay draws it instead, so leaving it in `linesFC`
  // too would double-render it and let the click router's lines hit layer
  // fight the session for the same clicks.
  const drawnLines = useMemo(
    // CLUB-676: a line hidden from the collection drops out here too, which
    // takes its casing, interior, hit target, arrowhead and name label with
    // it — they all read from `drawnLines`.
    //
    // The arrows and lines switches drop a line here too, except on that
    // kind's own tool tab — the layer being edited is always drawn, as the
    // ground layers do for sub-areas and forbidden areas.
    () => lines.filter(
      (l) =>
        l.points.length >= 2 &&
        l.id !== vertexEditLineId &&
        !hiddenObjectIds?.has(l.id) &&
        isLineKindVisible({ arrowsVisible: arrowsShown, linesVisible: linesShown }, l.kind),
    ),
    [lines, vertexEditLineId, hiddenObjectIds, arrowsShown, linesShown],
  )
  const linesFC = useMemo(
    () => lineFeatureCollection(drawnLines, { lines: lt.lines, arrows: lt.arrows }),
    [drawnLines, lt.lines, lt.arrows],
  )
  const linesClickable = !blockInteraction && !!onLineSelect

  const arrowLines = drawnLines.filter((l) => l.kind === 'arrow')

  return (
    <>
      {LINE_STYLES.map((style) => {
        // The casing draws LINE_BORDER_FACTOR wider than the interior, and a
        // MapLibre dasharray unit is a multiple of the LAYER's OWN
        // line-width — so the casing's pattern must be divided by that same
        // factor to land on the same physical dash/gap length as the
        // interior's (see lineDashPattern's own doc comment).
        const casingDash = lineDashPattern(style, LINE_BORDER_FACTOR)
        const interiorDash = lineDashPattern(style)
        const filter = ['==', ['get', 'style'], style]
        const layout = { 'line-cap': 'butt', 'line-join': 'miter' }
        return [
          <GeoJsonLineLayer
            key={`${style}-casing`}
            id={LINES_CASING_ID(style)}
            data={linesFC}
            paint={{ 'line-color': '#000', 'line-width': lineWidthExpression(LINE_BORDER_FACTOR), ...(casingDash ? { 'line-dasharray': casingDash } : {}) }}
            layout={layout}
            filter={filter}
          />,
          <GeoJsonLineLayer
            key={style}
            id={LINES_ID(style)}
            data={linesFC}
            paint={{ 'line-color': ['get', 'color'], 'line-width': lineWidthExpression(), ...(interiorDash ? { 'line-dasharray': interiorDash } : {}) }}
            layout={layout}
            filter={filter}
          />,
        ]
      })}

      {linesClickable && (
        <GeoJsonLineLayer id={LINES_HIT_ID} data={linesFC} paint={{ 'line-color': 'transparent', 'line-width': 16 }} />
      )}

      {arrowLines.map((line) => {
        const from = secondLastDistinctPoint(line.points)
        if (!from) return null
        const to = line.points[line.points.length - 1]
        const height = Math.min(lineStrokeWidthCssPx(line.width, zoom + MAPLIBRE_LAW_ZOOM_OFFSET, lt.arrows) * 5, ARROW_MAX_HEIGHT)
        return (
          <MapMarker key={line.id} lat={to.lat} lon={to.lon} anchor="center" rotation={dimOutside ? undefined : segmentBearingDeg(from, to)}>
            <PrintMarkerClip point={to} frame={dimOutside}><div style={{ transform: dimOutside ? `rotate(${segmentBearingDeg(from, to)}deg)` : undefined }}><ArrowHeadGlyph color={line.color ?? LINE_DEFAULT_COLOR} height={height} /></div></PrintMarkerClip>
          </MapMarker>
        )
      })}

      {appearance.pinNamesVisible && drawnLines.map((line) => <LineNameMarker key={line.id + '-label'} line={line} zoom={zoom} appearance={appearance} dimOutside={dimOutside} />)}

      {/* Pins — CLUB-667: filtered by type, the same set the read-only map and
          the printed sheet use. Sprite and label are drawn in this one pass, so
          dropping the object here takes both.

          CLUB-676: and a pin hidden from the collection is not drawn either.
          Two independent reasons to omit one, so both are checked — the list
          and the map must not disagree about what is on the ground, in the
          editor no less than on the Map tab. */}
      {objects
        .filter((o) => pinVisible(o.type) && !hiddenObjectIds?.has(o.id))
        .map((o) => {
        const isSelected = o.id === selectedId && activeTab === 'pins'
        const label = pinLabelColors(appearance.labelBackground)
        const fontSize = pinLabelCssPx(zoom, tileZoomMax)
        const spriteScale = dprScale > 0 ? pinScale / dprScale : pinScale
        // On a print preview the label slider decides, as it does on the paper.
        const showLabel = (printPreview ? printPreview.labelPx > 0 : appearance.pinNamesVisible) && !!o.label
        return (
          <MapMarker
            key={o.id}
            lat={o.lat}
            lon={o.lon}
            anchor="bottom"
            // Only the SELECTED pin moves: click a stand, then drag it. With
            // every pin draggable, pressing on a stand to pan the map picked it
            // up on the first pixel of movement, and users moved stands without
            // meaning to. Never while placing, drawing or editing vertices
            // (`blockInteraction`).
            draggable={!blockInteraction && isSelected}
            onDragEnd={blockInteraction || !isSelected ? undefined : (pos) => onMoveObj(o.id, pos.lat, pos.lon)}
          >
            {/* The opacity sits on this wrapper rather than on the sprite so
                the name label fades with the pin it belongs to. */}
            <PrintMarkerClip point={o} frame={dimOutside} testId={`editor-pin-${o.id}`}><div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              {showLabel && (
                <span
                  style={{
                    fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif',
                    fontSize,
                    lineHeight: 1.2,
                    fontWeight: 600,
                    color: label.text,
                    // The print draws names at full strength; label opacity is a viewing preference.
                    opacity: printPreview ? 1 : appearance.labelOpacity / 100,
                    whiteSpace: 'nowrap',
                    marginBottom: 2,
                    ...(printPreview
                      ? printPinLabelStyle(printPreview.labelPx)
                      : appearance.labelBackground
                      ? { background: label.pillFill, border: `1px solid ${label.pillBorder}`, borderRadius: 4, padding: '1px 5px' }
                      : { textShadow: `0 0 2px ${label.textHalo}, 0 0 2px ${label.textHalo}` }),
                  }}
                >
                  {o.label}
                </span>
              )}
              <svg width={20 * pinScale} height={21 * pinScale} viewBox={`${28 - 10 * pinScale} ${47 - 21 * pinScale} ${20 * pinScale} ${21 * pinScale}`} style={{ display: 'block', overflow: 'visible', marginBottom: -pinMarkerCentreLiftPx(o.type, appearance.pinBackground, pinScale) }}>
                <MapPinSprite
                  kind={o.type}
                  symbol={o.customSymbol}
                  x={28}
                  y={47}
                  withBackground={appearance.pinBackground}
                  selected={isSelected}
                  cursor={blockInteraction ? 'default' : 'pointer'}
                  scale={spriteScale}
                  color={o.color}
                  // The print draws every pin opaque; pin opacity is a viewing preference.
                  opacity={printPreview ? 1 : appearance.pinOpacity / 100}
                  interactive={!blockInteraction}
                  onClick={() => onSelectObj(o.id)}
                />
              </svg>
            </div></PrintMarkerClip>
          </MapMarker>
        )
      })}
    </>
  )
}
